<template>
  <div
    class="rounded-2xl p-3 md:px-4 md:py-6"
    :style="`height: ${isDesktop ? 294 : 282}px`"
    :class="{
      'bg-white-005': !daoPageInfo.isCustom.value,
      [daoPageInfo.vueState.customClass === 'ape'
        ? 'bg-ape-main'
        : 'bg-aavegotchi-main']: daoPageInfo.isCustom.value,
    }"
  >
    <div v-if="loading">
      <Spin></Spin>
    </div>

    <div class="text-whitebase h-full" v-else>
      <div class="mr-0 md:mr-4">
        <div class="flex justify-between items-center mb-2 md:mb-0">
          <div class="font-bold text-base md:text-xl">Treasury</div>
          <div class="flex justify-end">
            <div class="flex space-x-4">
              <div
                class="bar-item-btn font-bold"
                :class="{
                  'bg-inputActive border-inputActive': state.timeType == i,
                  ['border-pass-topbg bg-' + daoPageInfo.vueState.customClass]:
                    state.timeType == i && daoPageInfo.isCustom.value,
                }"
                v-for="(item, i) in ['7D', '1M', 'All']"
                :key="i"
                @click="state.timeType = i"
                :style="`min-width: auto; font-size: 12px;height:${
                  isDesktop ? 32 : 24
                }px`"
              >
                {{ item }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="flex-1 h-full min-w-0 flex flex-col md:flex-row"
        style="max-height: 214px"
      >
        <div
          class="
            flex flex-row
            md:flex-col
            justify-between
            md:justify-center
            space-y-0
            md:space-y-5
            mb-4
            md:mb-0
          "
        >
          <div v-for="(item, i) in TreasuryValues" :key="i" class="mr-3">
            <div class="text-xs text-white-070">{{ item.title }}</div>
            <SecondaryTitle
              :isPrimaryEth="daoPageInfo.vueState.byUnit.value === 'ETH'"
              primaryStyle="font-bold text-xs md:text-base mr-1"
              :isPrimaryMonetary="true"
              secondaryStyle="text-xs mb-0 md:mb-1"
              :primaryStat="item.value"
              :primaryToFixed="1"
              :secondaryStat="item.rate * 100"
              :isSecondarySuffix="true"
              :isSecondaryRate="true"
            />
          </div>
        </div>
        <div class="w-full flex-1 min-h-0" ref="container">
          <VChart
            ref="chart"
            :key="width"
            class="h-full"
            style="min-height: 150px"
            :option="lineOptions"
          ></VChart>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { queryDashbaordChat } from '@/api/dao';
import Spin from '@/components/Spin.vue';

import { DashboardDisplayType } from '@/constants';
import { useDesktop, useReqByBool } from '@/hooks';
import { formatVal, localeNumber } from '@/utils';
import SecondaryTitle from '@/views/LeaderBoard/SecondaryTitle.vue';
import { useElementSize } from '@vueuse/core';
import dayjs from 'dayjs';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
  computed,
  defineProps,
  inject,
  onMounted,
  reactive,
  watch,
  ref,
} from 'vue';
import VChart from 'vue-echarts';
import { useRoute } from 'vue-router';
const isDesktop = useDesktop();

const daoPageInfo = inject('daoPageInfo');
const $route = useRoute();

const chart = ref(null);
const container = ref(null);
const { width, height } = useElementSize(container);
const props = defineProps({
  type: String,
  isUsdt: Boolean,
});
const ftItem = {
  text: 'FT VAULT',
  color: daoPageInfo.isCustom.value
    ? daoPageInfo.vueState.customClass === 'ape'
      ? 'rgba(6, 35, 203, 1)'
      : 'rgba(250, 52, 242, 1)'
    : 'rgba(247, 37, 133, 1)',
};
const nftItem = {
  text: 'NFT VAULT',
  color: daoPageInfo.isCustom.value
    ? daoPageInfo.vueState.customClass === 'ape'
      ? 'rgba(255, 103, 5, 1)'
      : 'rgba(126, 24, 248, 1)'
    : 'rgba(147, 23, 181, 1)',
};
const isFloor = computed(() => daoPageInfo.vueState.byKey.value != 'ByHistory');
const state = reactive({
  type: String,
  records: {
    ft: [],
    nft: [],
  },
  loading: false,
  selectedIndex: 0,
  timeType: 0,
});

use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  GridComponent,
]);

const { loadData, loading } = useReqByBool(async () => {
  const x = await queryDashbaordChat(
    $route.params.name,
    daoPageInfo.vueState.includeOwnAssets,
  );

  console.log(x);

  state.records = x;
});

watch(() => daoPageInfo.vueState.includeOwnAssets, loadData);

onMounted(async () => {
  loadData();
  console.log(width);
});

const TreasuryValues = computed(() => {
  const {
    ft,
    nft,
    ftValueRate,
    ftValueUsdRate,
    nftValueRate,
    nftValueUsdRate,
    nftValueFloorRate,
    nftValueUsdFloorRate,
    totalValueRate,
    totalValueUsdRate,
    totalValueFloorRate,
    totalValueUsdFloorRate,
  } = state.records;

  let ftValue =
    ft.length > 0
      ? +(props.isUsdt ? ft[ft.length - 1].valueUsd : ft[ft.length - 1].value)
      : 0;
  let nftValue =
    nft.length > 0
      ? +(props.isUsdt
          ? isFloor.value
            ? nft[nft.length - 1].valueFloorUsd
            : nft[nft.length - 1].valueUsd
          : isFloor.value
          ? nft[nft.length - 1].valueFloor
          : nft[nft.length - 1].value)
      : 0;

  return [
    {
      title: 'Total',
      value: formatVal(ftValue + nftValue, { omit: true }),
      rate:
        (props.isUsdt
          ? isFloor.value
            ? totalValueUsdFloorRate
            : totalValueUsdRate
          : isFloor.value
          ? totalValueFloorRate
          : totalValueRate) || 0,
    },
    {
      title: 'NFT value',
      value: formatVal(nftValue, { omit: true }),
      rate:
        (props.isUsdt
          ? isFloor.value
            ? nftValueUsdFloorRate
            : nftValueUsdRate
          : isFloor.value
          ? nftValueFloorRate
          : nftValueRate) || 0,
    },
    {
      title: 'FT value',
      value: formatVal(ftValue, { omit: true }),
      rate: (props.isUsdt ? ftValueUsdRate : ftValueRate) || 0,
    },
  ];
});

const lineOptions = computed(() => {
  const num = state.timeType ? 30 : 7;
  /* - labels - */
  let labels = state.records[state.records.nft ? 'nft' : 'ft'].map(
    (x) => (x.timeText = dayjs(x.time).format('MM-DD')),
  );
  labels =
    state.timeType == 2 || num >= labels.length
      ? labels
      : labels.slice(labels.length - num, labels.length);

  /* - data - */
  const series = [];

  let ftList = state.records.ft;
  ftList =
    state.timeType == 2 || !ftList || num >= ftList.length
      ? ftList
      : ftList.slice(ftList.length - num, ftList.length);

  let nftList = state.records.nft;
  nftList =
    state.timeType == 2 || !nftList || num >= nftList.length
      ? nftList
      : nftList.slice(nftList.length - num, nftList.length);

  const color = [];

  if (props.type !== DashboardDisplayType.ONLY_NFT_COLLECTION) {
    color.push(ftItem.color);
    series.push({
      name: 'FT VAULT',
      type: 'line',
      data: ftList.map((x) => +(props.isUsdt ? x.valueUsd : x.value)),
      symbol: 'emptyCircle',
      showSymbol: ftList.length < 2 ? true : false,
      //   smooth: true,
      areaStyle: {
        opacity: 0,
      },
      // tooltip: {
      //   valueFormatter: (value) => value.toFixed(4) + ' ETH',
      // },
    });
  }

  if (props.type !== DashboardDisplayType.ONLY_FT_COLLECTION) {
    color.push(nftItem.color);
    series.push({
      name: 'NFT VAULT',
      type: 'line',
      data: nftList.map(
        (x) =>
          +(props.isUsdt
            ? isFloor.value
              ? x.valueFloorUsd
              : x.valueUsd
            : isFloor.value
            ? x.valueFloor
            : x.value),
      ),
      symbol: 'emptyCircle',
      showSymbol: nftList.length < 2 ? true : false,
      //   smooth: true,
      areaStyle: {
        opacity: 0,
      },
      // tooltip: {
      //   valueFormatter: (value) => value.toFixed(4) + ' ETH',
      // },
    });
  }

  //   if (
  //     selectedImtes.value.length == 3 &&
  //     series[0].label !== selectedImtes.value[1].text
  //   ) {
  //     series.reverse();
  //     color.reverse();
  //   }

  return {
    responsive: true,
    maintainAspectRatio: false,
    color,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      borderColor: '#FFFFFF66',
      backgroundColor: '#1e1f21',
      textStyle: {
        color: '#fff',
      },
      formatter: (data) => {
        let list;

        const htmls = data.map((data) => {
          const isFt = data.seriesName == 'FT VAULT';
          const datas = data.seriesName == 'FT VAULT' ? ftList : nftList;
          list = datas;

          return `<div class="text-xs space-y-2">
              <div class="flex items-center">
                <div class="w-2 h-2 rounded-full mr-2" style="background:${
                  data.color
                }"></div> ${data.seriesName}
                </div>
              Valuation: <span> ${props.isUsdt ? '$' : ''}${localeNumber(
            isFt
              ? props.isUsdt
                ? datas[data.dataIndex].valueUsd
                : datas[data.dataIndex].value
              : props.isUsdt
              ? isFloor.value
                ? datas[data.dataIndex].valueFloorUsd
                : datas[data.dataIndex].valueUsd
              : isFloor.value
              ? datas[data.dataIndex].valueFloor
              : datas[data.dataIndex].value,
            3,
          )}${props.isUsdt ? '' : 'ETH'}</span><br/>In: <span>${
            datas[data.dataIndex].in
          }</span><br/>Out: <span>${datas[data.dataIndex].out}</span></div>`;
        });

        const dataIndex = data[0].dataIndex;

        return (
          `<div class="space-y-2"><span class="font-bold mt-2 text-xs">Total: ${
            props.isUsdt ? '$' : ''
          }${localeNumber(
            props.isUsdt
              ? +ftList[dataIndex].valueUsd +
                  (isFloor.value
                    ? +nftList[dataIndex].valueFloorUsd
                    : +nftList[dataIndex].valueUsd)
              : +ftList[dataIndex].value +
                  (isFloor.value
                    ? +nftList[dataIndex].valueFloor
                    : +nftList[dataIndex].value),
            3,
          )}${props.isUsdt ? '' : 'ETH'}</span>` +
          htmls.join('') +
          `<span class="font-bold mt-2 text-xs">${dayjs(
            list[data[0].dataIndex].time,
          ).format('YYYY-MM-DD')}</span></div>`
        );
      },
    },
    yAxis: {
      splitLine: {
        show: false,
      },
      type: 'value',
      axisLabel: {
        formatter(val) {
          return formatVal(val, { omit: true });
        },
      },
    },
    xAxis: [{ type: 'category', data: labels }],
    grid: {
      top: '4%',
      left: '4%',
      right: '4%',
      bottom: '4%',
      containLabel: true,
    },
    // grid: isDesktop
    //   ? {
    //       top: '4%',
    //       left: '20%',
    //       right: '4%',
    //       bottom: '10%',
    //     }
    //   : {
    //       top: '4%',
    //       left: '10%',
    //       right: '4%',
    //       bottom: '15%',
    //     },
    series,
  };
});
</script>

<style scoped>
.selected-box {
  border: 1px solid transparent;
  transition: all 0.3s;
}
.selected-box__active {
  border: 1px solid #fff;
}
</style>
